<template>
    <div
        class="canvas"
        :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
        data-testid="ds-zoomable-canvas"
        @mousedown.self="beginDrag"
    >
        <div class="absolute left-8 top-6 z-[1] flex items-center">
            <div class="rounded-lg bg-white">
                <DsTooltip tooltipText="Zoom out">
                    <DsButton
                        class="w-10 gap-0 rounded-r-none border border-r-0 border-ds-primary-200 p-0"
                        :disabled="zoom <= minZoom"
                        leading="fa-solid fa-minus"
                        size="md"
                        variant="icon"
                        @click="zoomOut"
                    />
                </DsTooltip>
                <DsTooltip tooltipText="Zoom in">
                    <DsButton
                        class="w-10 gap-0 rounded-l-none border border-ds-primary-200 p-0"
                        :disabled="zoom >= maxZoom"
                        leading="fa-solid fa-plus"
                        size="md"
                        variant="icon"
                        @click="zoomIn"
                    />
                </DsTooltip>
            </div>
            <div class="ml-4 rounded-lg bg-white">
                <DsButton
                    class="border border-ds-primary-200"
                    size="md"
                    variant="secondary"
                    @click="resetCanvas"
                >
                    Reset
                </DsButton>
            </div>
        </div>
        <div
            class="absolute cursor-auto"
            :style="{ transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})` }"
        >
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { DRAG_INTERACTIONS, type MousePosition, useDragInteraction } from '@/components/design-system/canvas/useDragInteraction';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsTooltip from '@/components/design-system/tooltip/DsTooltip.vue';
import { ref } from 'vue';

interface DsZoomableCanvasProps {
    initialPosition?: MousePosition;
    minZoom?: number;
    maxZoom?: number;
}

const INITIAL_ZOOM = 1;

const props = withDefaults(defineProps<DsZoomableCanvasProps>(), {
    minZoom: DRAG_INTERACTIONS.ZOOM.MIN,
    maxZoom: DRAG_INTERACTIONS.ZOOM.MAX,
});

const { position, isDragging, beginDrag, resetPosition } = useDragInteraction({
    initialPosition: props.initialPosition,
});

// Zoom
const zoom = ref(INITIAL_ZOOM);

const zoomIn = () => {
    zoom.value = Math.min(zoom.value + DRAG_INTERACTIONS.ZOOM.BUTTON_STEP, props.maxZoom);
};

const zoomOut = () => {
    zoom.value = Math.max(zoom.value - DRAG_INTERACTIONS.ZOOM.BUTTON_STEP, props.minZoom);
};

const resetCanvas = () => {
    resetPosition();
    zoom.value = INITIAL_ZOOM;
};
</script>

<style lang="scss" scoped>
.canvas {
    @apply relative flex h-full items-center justify-center overflow-hidden;
    background-image: radial-gradient(theme('colors.ds.gray.300'), theme('colors.ds.gray.300') 1px, transparent 2px);
    background-size: 24px 24px;
}
</style>
